import dayjs from '@/lib/dayjs'
import { Dayjs } from 'dayjs'

export const FOLLOW_UP_TYPE = {
  FIRST_CONTACT: 'FIRST_CONTACT',
  SERVICE_CALL: 'SERVICE_CALL',
  FOLLOWUP: 'FOLLOWUP',
  RENEWAL_REMINDER: 'RENEWAL_REMINDER',
  SPECIAL_EVENT: 'SPECIAL_EVENT',
  NOTE: 'NOTE',
} as const

export type FollowUpType = (typeof FOLLOW_UP_TYPE)[keyof typeof FOLLOW_UP_TYPE]

export const FOLLOW_UP_OUTCOME = {
  INTERESTED: 'INTERESTED',
  NEUTRAL: 'NEUTRAL',
  NOT_INTERESTED: 'NOT_INTERESTED',
  NO_RESPONSE: 'NO_RESPONSE',
} as const

export type FollowUpOutcome = (typeof FOLLOW_UP_OUTCOME)[keyof typeof FOLLOW_UP_OUTCOME]

export interface FollowUpTypeOption {
  value: FollowUpType
  labelEn: string
  labelTh: string
}

export const FOLLOW_UP_TYPE_OPTIONS: FollowUpTypeOption[] = [
  { value: FOLLOW_UP_TYPE.FIRST_CONTACT, labelEn: 'First Contact', labelTh: 'ติดต่อครั้งแรก' },
  { value: FOLLOW_UP_TYPE.SERVICE_CALL, labelEn: 'Service Call', labelTh: 'โทรบริการ' },
  { value: FOLLOW_UP_TYPE.FOLLOWUP, labelEn: 'Follow-up', labelTh: 'ติดตามผล' },
  { value: FOLLOW_UP_TYPE.RENEWAL_REMINDER, labelEn: 'Renewal Reminder', labelTh: 'แจ้งต่ออายุ' },
  { value: FOLLOW_UP_TYPE.SPECIAL_EVENT, labelEn: 'Special Event', labelTh: 'กิจกรรมพิเศษ' },
  { value: FOLLOW_UP_TYPE.NOTE, labelEn: 'Note', labelTh: 'โน้ต' },
]

export interface FollowUpOutcomeOption {
  value: FollowUpOutcome
  labelEn: string
  labelTh: string
}

export const FOLLOW_UP_OUTCOME_OPTIONS: FollowUpOutcomeOption[] = [
  { value: FOLLOW_UP_OUTCOME.INTERESTED, labelEn: 'Interested', labelTh: 'ตอบรับดี' },
  { value: FOLLOW_UP_OUTCOME.NEUTRAL, labelEn: 'Neutral', labelTh: 'เฉยๆ' },
  { value: FOLLOW_UP_OUTCOME.NOT_INTERESTED, labelEn: 'Not interested', labelTh: 'ไม่สนใจ' },
  { value: FOLLOW_UP_OUTCOME.NO_RESPONSE, labelEn: 'No response', labelTh: 'ไม่ตอบ' },
]

/**
 * Card badge colours for a recorded outcome (QTY-2095): good → green,
 * lukewarm → orange, anything else → grey. A red badge is reserved for
 * "overdue", so a negative answer must not compete with it.
 */
export const OUTCOME_COLOR: Record<FollowUpOutcome, 'success' | 'warning' | 'default'> = {
  INTERESTED: 'success',
  NEUTRAL: 'warning',
  NOT_INTERESTED: 'default',
  NO_RESPONSE: 'default',
}

export const followUpTypeLabel = (
  type: FollowUpType | null | undefined,
  isThai: boolean
): string => {
  const option = FOLLOW_UP_TYPE_OPTIONS.find((o) => o.value === type)
  if (!option) return ''
  return isThai ? option.labelTh : option.labelEn
}

export const followUpOutcomeLabel = (
  outcome: FollowUpOutcome | null | undefined,
  isThai: boolean
): string => {
  const option = FOLLOW_UP_OUTCOME_OPTIONS.find((o) => o.value === outcome)
  if (!option) return ''
  return isThai ? option.labelTh : option.labelEn
}

// BE validates against Prisma enum TS keys (UPPERCASE); Prisma auto-maps to
// the lowercase @map values at the SQL layer, so we send/receive UPPERCASE.
export const toBackendFollowUpType = (
  value: FollowUpType | null | undefined
): FollowUpType | undefined => value ?? undefined

export const fromBackendFollowUpType = (value: string | null | undefined): FollowUpType | null => {
  if (!value) return null
  const upper = value.toUpperCase() as FollowUpType
  return upper in FOLLOW_UP_TYPE ? upper : null
}

export const toBackendFollowUpOutcome = (
  value: FollowUpOutcome | null | undefined
): FollowUpOutcome | undefined => value ?? undefined

export const fromBackendFollowUpOutcome = (
  value: string | null | undefined
): FollowUpOutcome | null => {
  if (!value) return null
  const upper = value.toUpperCase() as FollowUpOutcome
  return upper in FOLLOW_UP_OUTCOME ? upper : null
}

/**
 * Status groups shared by the follow-up card, both CRM tables and the to-do
 * list (QTY-2095..2097). A record in the closed group is finished business:
 * its pending appointments are shown as "not followed up", hidden from the
 * table's appointment column and never counted as overdue.
 */
export const OPEN_RECORD_STATUSES = ['INCOMPLETED', 'LEAD', 'PROSPECT', 'OPEN_OPPORTUNITY'] as const
export const CLOSED_RECORD_STATUSES = ['MEMBER', 'UNQUALIFIED', 'CLOSE_LOST', 'CLOSE_WON'] as const

export const isClosedRecordStatus = (status: string | null | undefined): boolean =>
  !!status && (CLOSED_RECORD_STATUSES as readonly string[]).includes(status.toUpperCase())

/** Whole days from today to `date` (club timezone): negative = overdue. */
export const daysFromToday = (date: Dayjs | string | Date): number =>
  dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'day')

export const FOLLOW_UP_DATE_FORMAT = 'D MMM YYYY'
export const FOLLOW_UP_DATETIME_FORMAT = 'D MMM YYYY HH:mm'
